import OpenAIClient, { OpenAI as OpenAITypes, toFile } from "openai"
import { LLMFullResponse, LLMStreamChunk } from "../../types/ai"
import { LLM } from "./base"
import { LLMRequest } from "../llm"
import {
  ImageContentTypes,
  LLMConfigOptions,
  ResponseFormat,
  ToolMessage,
} from "@budibase/types"
import openai from "openai"
import { zodFunction } from "openai/helpers/zod"
import { Readable } from "node:stream"
import { normalizeContentType } from "../utils"

export type OpenAIModel =
  | "gpt-4o-mini"
  | "gpt-4o"
  | "gpt-4"
  | "gpt-4-turbo"
  | "gpt-3.5-turbo"

export function parseResponseFormat(
  responseFormat?: ResponseFormat
):
  | openai.ResponseFormatText
  | openai.ResponseFormatJSONObject
  | openai.ResponseFormatJSONSchema
  | undefined {
  if (!responseFormat) {
    return
  }

  if (responseFormat === "text") {
    return { type: "text" }
  }
  if (responseFormat === "json") {
    return { type: "json_object" }
  }

  return responseFormat
}

function calculateBudibaseAICredits(
  usage?: OpenAIClient.CompletionUsage
): number {
  if (!usage) {
    return 0
  }

  // Output tokens are 3 times more expensive in OpenAI
  // We want to have a single figure in BB for simplicity
  const inputTokens = usage.prompt_tokens
  const outputTokens = usage.completion_tokens
  return outputTokens * 3 + inputTokens
}

export class OpenAI extends LLM {
  protected client: OpenAIClient
  constructor(opts: LLMConfigOptions) {
    super(opts)
    this.client = this.getClient(opts)
  }

  protected getClient(opts: LLMConfigOptions): OpenAIClient {
    if (!opts.apiKey) {
      throw new Error("No OpenAI API key found")
    }
    return new OpenAIClient({ apiKey: opts.apiKey })
  }

  async uploadFile(
    data: Readable | Buffer,
    filename: string,
    contentType?: string
  ): Promise<string> {
    const normalizedContentType = normalizeContentType(contentType)

    // If it's an image we need to convert it to a base64 string.
    if (ImageContentTypes.includes(normalizedContentType.toLowerCase())) {
      let buffer: Buffer
      if (Buffer.isBuffer(data)) {
        buffer = data
      } else {
        const chunks: Buffer[] = []
        for await (const chunk of data as Readable) {
          chunks.push(chunk)
        }
        buffer = Buffer.concat(chunks)
      }
      const base64 = buffer.toString("base64")
      return `data:${normalizedContentType};base64,${base64}`
    }

    // For documents, use the files API
    const file = await toFile(data, filename)
    const res = await this.client.files.create({ file, purpose: "assistants" })
    return res.id
  }

  protected async chatCompletion(
    request: LLMRequest
  ): Promise<LLMFullResponse> {
    const parameters: OpenAITypes.ChatCompletionCreateParamsNonStreaming = {
      model: this.model,
      messages: request.messages,
      max_tokens: this.maxTokens,
      response_format: parseResponseFormat(request.format),
    }

    if (request.tools && request.tools.length > 0) {
      parameters.tool_choice = "auto"
      parameters.tools = request.tools.map(zodFunction)
    }

    const completion = await this.client.chat.completions.create(parameters)

    const response = completion.choices[0]?.message
    if (response?.tool_calls) {
      // Tool calls must be appended to the prompt messages otherwise you get
      // a 400 from OpenAI saying tool responses must be proceeded by tool call
      // messages.
      request.addMessage(response)

      let toolCalls: Promise<ToolMessage>[] = []
      for (const toolCall of response.tool_calls) {
        const tool = request.findTool(toolCall.function.name)
        if (!tool) {
          throw new Error(
            `Tool ${toolCall.function.name} not found in prompt tools`
          )
        }

        const args = JSON.parse(toolCall.function.arguments)
        toolCalls.push(
          tool
            .handler(args)
            .then((result): ToolMessage => {
              return {
                role: "tool",
                tool_call_id: toolCall.id,
                content: result,
              }
            })
            .catch((error): ToolMessage => {
              console.error(
                `[OPENAI TOOL ERROR] Tool ${toolCall.function.name} failed in OpenAI handler:`,
                error
              )
              return {
                role: "tool",
                tool_call_id: toolCall.id,
                content: `Error: ${error.message}`,
              }
            })
        )
      }

      request.addMessages(await Promise.all(toolCalls))
      return this.chatCompletion(request)
    } else if (response?.content) {
      return {
        messages: [
          ...request.messages,
          { role: response.role, content: response.content },
        ],
        tokensUsed: calculateBudibaseAICredits(completion.usage),
      }
    } else {
      throw new Error("No response found")
    }
  }

  protected async *chatCompletionStream(
    request: LLMRequest
  ): AsyncGenerator<LLMStreamChunk, void, unknown> {
    const parameters: OpenAITypes.ChatCompletionCreateParamsStreaming = {
      model: this.model,
      messages: request.messages,
      max_tokens: this.maxTokens,
      response_format: parseResponseFormat(request.format),
      stream: true,
    }

    if (request.tools && request.tools.length > 0) {
      parameters.tool_choice = "auto"
      parameters.tools = request.tools.map(zodFunction)
    }

    try {
      const stream = await this.client.chat.completions.create(parameters)

      let currentToolCall: {
        id: string
        name: string
        arguments: string
      } | null = null
      let contentBuffer = ""
      let finalUsage: OpenAIClient.CompletionUsage | null = null

      for await (const chunk of stream) {
        const delta = chunk.choices[0]?.delta
        if (!delta) continue

        // Handle content streaming
        if (delta.content) {
          contentBuffer += delta.content
          yield {
            type: "content",
            content: delta.content,
          }
        }

        // Handle tool calls
        if (delta.tool_calls && delta.tool_calls.length > 0) {
          const toolCall = delta.tool_calls[0]

          if (toolCall.id) {
            // New tool call
            currentToolCall = {
              id: toolCall.id,
              name: toolCall.function?.name || "",
              arguments: toolCall.function?.arguments || "",
            }

            yield {
              type: "tool_call_start",
              toolCall: {
                id: currentToolCall.id,
                name: currentToolCall.name,
                arguments: currentToolCall.arguments,
              },
            }
          } else if (currentToolCall && toolCall.function?.arguments) {
            // Existing tool call
            currentToolCall.arguments += toolCall.function.arguments

            yield {
              type: "tool_call_start",
              toolCall: {
                id: currentToolCall.id,
                name: currentToolCall.name,
                arguments: currentToolCall.arguments,
              },
            }
          }
        }

        if (chunk.usage) {
          finalUsage = chunk.usage
        }
      }

      const totalTokens = finalUsage
        ? calculateBudibaseAICredits(finalUsage)
        : 0

      // Execute the actual tool calls
      if (currentToolCall) {
        request.addMessage({
          role: "assistant",
          content: "",
          tool_calls: [
            {
              id: currentToolCall.id,
              type: "function",
              function: {
                name: currentToolCall.name,
                arguments: currentToolCall.arguments,
              },
            },
          ],
        })

        const tool = request.findTool(currentToolCall.name)
        if (tool) {
          try {
            const args = JSON.parse(currentToolCall.arguments)
            const result = await tool.handler(args)

            yield {
              type: "tool_call_result",
              toolResult: {
                id: currentToolCall.id,
                result: result,
              },
            }

            request.addMessage({
              role: "tool",
              tool_call_id: currentToolCall.id,
              content: result,
            })

            // Continue conversation after tool call
            yield* this.chatCompletionStream(request)
            return
          } catch (error: any) {
            yield {
              type: "tool_call_result",
              toolResult: {
                id: currentToolCall.id,
                result: `Error: ${error.message}`,
                error: error.message,
              },
            }

            request.addMessage({
              role: "tool",
              tool_call_id: currentToolCall.id,
              content: `Error: ${error.message}`,
            })

            // Continue conversation after tool error
            yield* this.chatCompletionStream(request)
            return
          }
        } else {
          const errorMessage = `Tool '${currentToolCall.name}' not found`

          yield {
            type: "tool_call_result",
            toolResult: {
              id: currentToolCall.id,
              result: errorMessage,
              error: errorMessage,
            },
          }

          request.addMessage({
            role: "tool",
            tool_call_id: currentToolCall.id,
            content: errorMessage,
          })

          // Continue conversation after tool error
          yield* this.chatCompletionStream(request)
          return
        }
      }

      // Final response
      if (contentBuffer) {
        request.addMessage({
          role: "assistant",
          content: contentBuffer,
        })
      }

      yield {
        type: "done",
        messages: request.messages,
        tokensUsed: totalTokens,
      }
    } catch (error: any) {
      yield {
        type: "error",
        content: error.message,
      }
    }
  }
}
